import { ReactNode, useId } from 'react';
import { Check, Info } from 'lucide-react';
import { Card } from './Card';
import { Typography } from './Typography';
import styles from './RadioGroup.module.css';

// THE single-select radio list (and its multi-select `CheckboxGroup` sibling) for the whole app —
// Figma "⚠️ Symphony DS" → Radio Group (node 1166-10334). Never hand-roll a ring/box + check beside a
// label; see CLAUDE.md → the Maestro-page notes.
//
// The Figma component is one control with these axes, mapped to props (MUI-flavoured, per the DS
// convention the other primitives follow):
//   type            → radio (circle) | checkbox (square)   → `RadioGroup` vs `CheckboxGroup`
//   Skin            → plain row | sunken Card wrapper       → `variant`
//   Orientation     → vertical | horizontal                → `orientation`
//   Text Placement  → indicator leading | trailing         → `indicatorPlacement`
//   Label / ⓘ       → optional group label + info glyph     → `label` / `info`
// Per-option `label` (Figma Title) + `description` (Subtitle); `disabled` greys a single option, and
// the group-level `disabled` greys them all.
//
// The mark is `SelectionIndicator`, exported on its own for the rare row that keeps bespoke layout
// (the voice card's rows carry a per-row sample button + hairlines, so they reuse only the mark).

export type SelectionShape = 'radio' | 'checkbox';
export type RadioGroupVariant = 'plain' | 'card';
export type RadioGroupOrientation = 'vertical' | 'horizontal';
export type IndicatorPlacement = 'leading' | 'trailing';

/** The decorative selection mark. State lives on the input/button that owns it. */
export function SelectionIndicator({
  checked,
  shape = 'radio',
  disabled,
  className,
}: {
  checked: boolean;
  shape?: SelectionShape;
  disabled?: boolean;
  className?: string;
}) {
  const classes = [
    styles.indicator,
    shape === 'checkbox' ? styles.checkboxShape : styles.radioShape,
    checked && styles.indicatorChecked,
    disabled && styles.indicatorDisabled,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <span className={classes} aria-hidden>
      {checked && <Check size={12} strokeWidth={2.5} />}
    </span>
  );
}

export interface RadioOption<T extends string | number> {
  value: T;
  /** Figma Title. */
  label: ReactNode;
  /** Figma Subtitle — the secondary line under the label. */
  description?: ReactNode;
  disabled?: boolean;
}

interface ChoiceGroupBaseProps<T extends string | number> {
  options: RadioOption<T>[];
  /** Radio inputs share this `name`; auto-generated when omitted. */
  name?: string;
  /** Figma "Label" — the group's own heading. */
  label?: ReactNode;
  /** Figma "ⓘ" — an info glyph beside the label; its text becomes the tooltip + accessible name. */
  info?: string;
  variant?: RadioGroupVariant;
  orientation?: RadioGroupOrientation;
  /** Default: `trailing` for `card`, `leading` for `plain`. */
  indicatorPlacement?: IndicatorPlacement;
  /** Greys and blocks every option. */
  disabled?: boolean;
  /** When false the inputs are not tab-reachable — e.g. inside a collapsed panel. Default true. */
  tabbable?: boolean;
  /** Accessible name when there is no visible `label`. */
  'aria-label'?: string;
  className?: string;
  /** Extra class on each option row. */
  itemClassName?: string;
}

interface ChoiceGroupProps<T extends string | number> extends ChoiceGroupBaseProps<T> {
  shape: SelectionShape;
  inputType: 'radio' | 'checkbox';
  role: 'radiogroup' | 'group';
  isChecked: (value: T) => boolean;
  onToggle: (value: T) => void;
}

function ChoiceGroup<T extends string | number>({
  options,
  name,
  label,
  info,
  variant = 'plain',
  orientation = 'vertical',
  indicatorPlacement,
  disabled,
  tabbable = true,
  'aria-label': ariaLabel,
  className,
  itemClassName,
  shape,
  inputType,
  role,
  isChecked,
  onToggle,
}: ChoiceGroupProps<T>) {
  const generatedName = useId();
  const groupName = name ?? generatedName;
  const labelId = label != null ? `${groupName}-label` : undefined;
  const placement = indicatorPlacement ?? (variant === 'card' ? 'trailing' : 'leading');

  const list = (
    <div
      role={role}
      aria-label={labelId ? undefined : ariaLabel}
      aria-labelledby={labelId}
      className={[
        styles.list,
        variant === 'card' && styles.cardList,
        orientation === 'horizontal' ? styles.horizontal : styles.vertical,
        // With no group label the list IS the outermost node, so the caller's class lands here.
        label == null ? className : null,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {options.map((opt) => {
        const checked = isChecked(opt.value);
        const isDisabled = Boolean(disabled || opt.disabled);
        const indicator = <SelectionIndicator checked={checked} shape={shape} disabled={isDisabled} />;
        // Literal `type` (never a computed `type={…}`) so the input-spec guard can see this is a
        // radio/checkbox, not a text field.
        const inputProps = {
          className: styles.input,
          checked,
          disabled: isDisabled,
          tabIndex: tabbable ? undefined : -1,
          onChange: () => onToggle(opt.value),
        };
        const input =
          inputType === 'radio' ? (
            <input type="radio" name={groupName} {...inputProps} />
          ) : (
            <input type="checkbox" {...inputProps} />
          );
        const body = (
          <>
            {input}
            {placement === 'leading' && indicator}
            <span className={styles.text}>
              {variant === 'card' ? (
                <Typography variant="h3" color="primary" component="span">
                  {opt.label}
                </Typography>
              ) : (
                <span className={styles.title}>{opt.label}</span>
              )}
              {opt.description != null && (
                <Typography variant="p2" color="secondary" component="span">
                  {opt.description}
                </Typography>
              )}
            </span>
            {placement === 'trailing' && indicator}
          </>
        );

        const rowClass = [styles.item, isDisabled && styles.itemDisabled, itemClassName]
          .filter(Boolean)
          .join(' ');

        return variant === 'card' ? (
          <Card
            key={String(opt.value)}
            component="label"
            variant="sunken"
            size="sm"
            interactive
            selected={checked}
            className={rowClass}
          >
            {body}
          </Card>
        ) : (
          <label key={String(opt.value)} className={rowClass}>
            {body}
          </label>
        );
      })}
    </div>
  );

  if (label == null) return list;

  return (
    <div className={[styles.group, className].filter(Boolean).join(' ')}>
      {label != null && (
        <span className={styles.groupLabel} id={labelId}>
          <Typography variant="h4" color="strong" component="span">
            {label}
          </Typography>
          {info != null && (
            <span className={styles.info} title={info} role="img" aria-label={info}>
              <Info size={14} aria-hidden />
            </span>
          )}
        </span>
      )}
      {list}
    </div>
  );
}

export interface RadioGroupProps<T extends string | number> extends ChoiceGroupBaseProps<T> {
  value: T | null;
  onChange: (value: T) => void;
}

/** Single-select. Exactly one option is chosen at a time (`role="radiogroup"`). */
export function RadioGroup<T extends string | number>({ value, onChange, ...rest }: RadioGroupProps<T>) {
  return (
    <ChoiceGroup
      {...rest}
      shape="radio"
      inputType="radio"
      role="radiogroup"
      isChecked={(v) => v === value}
      onToggle={onChange}
    />
  );
}

export interface CheckboxGroupProps<T extends string | number> extends ChoiceGroupBaseProps<T> {
  value: T[];
  onChange: (value: T[]) => void;
}

/** Multi-select. Any number of options may be chosen (`role="group"` of checkboxes). */
export function CheckboxGroup<T extends string | number>({ value, onChange, ...rest }: CheckboxGroupProps<T>) {
  return (
    <ChoiceGroup
      {...rest}
      shape="checkbox"
      inputType="checkbox"
      role="group"
      isChecked={(v) => value.includes(v)}
      onToggle={(v) => (value.includes(v) ? onChange(value.filter((x) => x !== v)) : onChange([...value, v]))}
    />
  );
}
